/**
 * Fishlove sticky add-to-cart: variant sync + cross-browser scroll visibility.
 *
 * Desktop uses position:sticky on product details, so buy-buttons stay in view
 * and IntersectionObserver never fires. We track the whole product block instead.
 */
(function () {
  const SELECTOR = '[data-fishlove-sticky-variant-select]';

  /**
   * @param {HTMLElement | null} section
   * @param {string} variantId
   */
  function applyVariantInSection(section, variantId) {
    if (!section || !variantId) return;

    const picker = section.querySelector('variant-picker');
    if (!picker) return;

    const match = picker.querySelector(`[data-variant-id="${variantId}"]`);
    if (!match) return;

    if (match instanceof HTMLOptionElement) {
      const select = match.closest('select');
      if (!select) return;
      select.value = match.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (match instanceof HTMLInputElement && match.type === 'radio') {
      match.checked = true;
      match.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @returns {EventTarget[]} */
  function getScrollRoots() {
    const roots = [window];
    const pageWrapper = document.querySelector('.page-wrapper');

    // Horizon scrolls .page-wrapper on desktop (>=990px), not window — see base.css
    if (pageWrapper) roots.push(pageWrapper);

    return roots;
  }

  /** @returns {HTMLElement | null} */
  function getPageFooter() {
    return (
      document.querySelector('.page-wrapper > footer') ||
      document.querySelector('.shopify-section-group-footer-group footer') ||
      document.querySelector('[class*="footer-group"] footer') ||
      null
    );
  }

  /** @param {HTMLElement} sticky */
  function getStickySection(sticky) {
    const sectionId = sticky.dataset.fishloveSectionId;
    if (sectionId) {
      return document.getElementById(`shopify-section-${sectionId}`);
    }

    return sticky.closest('.shopify-section');
  }

  /** @param {HTMLElement} sticky */
  function portalStickyToBody(sticky) {
    if (sticky.dataset.fishlovePortaled === 'true') return;

    const section = sticky.closest('.shopify-section');
    if (section?.id && !sticky.dataset.fishloveSectionId) {
      sticky.dataset.fishloveSectionId = section.id.replace('shopify-section-', '');
    }

    // Horizon scrolls .page-wrapper on desktop — fixed inside it pins to the top on Safari/Mac
    document.body.appendChild(sticky);
    sticky.dataset.fishlovePortaled = 'true';
  }

  /** @param {HTMLElement} bar */
  function pinBarToBottom(bar) {
    const desktop = window.matchMedia('(min-width: 990px)').matches;

    bar.style.setProperty('position', 'fixed', 'important');
    bar.style.setProperty('top', 'auto', 'important');
    bar.style.setProperty('bottom', desktop ? '16px' : '0', 'important');
    bar.style.setProperty('left', '0', 'important');
    bar.style.setProperty('right', '0', 'important');
    bar.style.setProperty('margin-left', 'auto', 'important');
    bar.style.setProperty('margin-right', 'auto', 'important');

    if (desktop) {
      bar.style.setProperty('width', 'min(960px, calc(100% - 2rem))', 'important');
      bar.style.setProperty('max-width', '960px', 'important');
    } else {
      bar.style.removeProperty('width');
      bar.style.removeProperty('max-width');
    }
  }

  /**
   * @param {HTMLElement} sticky
   */
  function bindFishloveVisibility(sticky) {
    if (sticky.dataset.fishloveVisibilityBound === 'true') return;

    const bar = sticky.querySelector('.sticky-add-to-cart__bar');
    const section = getStickySection(sticky);
    const buyButtons = section?.querySelector('.buy-buttons-block');
    const productRoot = section?.querySelector('featured-product-information, .product-information');
    const sentinel = section?.querySelector('[data-fishlove-atc-sentinel]');
    const footer = getPageFooter();

    if (!bar || (!buyButtons && !productRoot && !sentinel)) return;

    sticky.dataset.fishloveVisibilityBound = 'true';

    pinBarToBottom(bar);

    let scrolledPastProduct = false;
    let footerInView = false;
    let ticking = false;

    const getHeaderHeight = () =>
      Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;

    const apply = () => {
      pinBarToBottom(bar);
      bar.dataset.stuck = scrolledPastProduct && !footerInView ? 'true' : 'false';
    };

    const updateFromRects = () => {
      ticking = false;

      const header = getHeaderHeight();

      if (buyButtons) {
        const buyRect = buyButtons.getBoundingClientRect();
        scrolledPastProduct = buyRect.bottom < header + 12;
      } else if (sentinel) {
        const sentinelRect = sentinel.getBoundingClientRect();
        scrolledPastProduct = sentinelRect.bottom < header + 12;
      } else if (productRoot) {
        const productRect = productRoot.getBoundingClientRect();
        scrolledPastProduct = productRect.bottom < header + 12;
      }

      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        footerInView = footerRect.top < window.innerHeight * 0.9;
      }

      apply();
    };

    const scheduleRectUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateFromRects);
    };

    getScrollRoots().forEach((root) => {
      root.addEventListener('scroll', scheduleRectUpdate, { passive: true });
    });
    window.addEventListener('resize', scheduleRectUpdate, { passive: true });
    window.addEventListener('orientationchange', scheduleRectUpdate, { passive: true });

    scheduleRectUpdate();
  }

  function initFishloveSticky() {
    document.querySelectorAll('sticky-add-to-cart.fishlove-sticky-atc').forEach((sticky) => {
      if (sticky instanceof HTMLElement) {
        portalStickyToBody(sticky);
        bindFishloveVisibility(sticky);
      }
    });
  }

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || !target.matches(SELECTOR)) return;

    const sticky = target.closest('sticky-add-to-cart.fishlove-sticky-atc');
    const section =
      sticky instanceof HTMLElement ? getStickySection(sticky) : target.closest('.shopify-section');
    applyVariantInSection(section, target.value);
  });

  document.addEventListener('product:select', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const section = target.classList.contains('shopify-section')
      ? target
      : target.closest('.shopify-section');
    if (!section) return;

    const sticky = section.querySelector('sticky-add-to-cart.fishlove-sticky-atc');
    if (!sticky) {
      const sectionKey = section.id.replace('shopify-section-', '');
      const portaled = document.querySelector(
        `sticky-add-to-cart.fishlove-sticky-atc[data-fishlove-section-id="${sectionKey}"]`
      );
      if (!(portaled instanceof HTMLElement)) return;
      syncStickyVariantFromEvent(portaled, event);
      return;
    }

    syncStickyVariantFromEvent(sticky, event);
  });

  /**
   * @param {HTMLElement} sticky
   * @param {Event} event
   */
  function syncStickyVariantFromEvent(sticky, event) {
    const productEvent = /** @type {CustomEvent & { promise?: Promise<{ detail?: { resource?: { id?: string|number } } }> }} */ (
      event
    );

    productEvent.promise
      ?.then(({ detail }) => {
        const variantId = detail?.resource?.id;
        const stickySelect = sticky.querySelector(SELECTOR);
        if (stickySelect instanceof HTMLSelectElement && variantId) {
          stickySelect.value = String(variantId);
        }
      })
      .catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFishloveSticky, { once: true });
  } else {
    initFishloveSticky();
  }

  document.addEventListener('shopify:section:load', initFishloveSticky);
})();
